// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import ScrapInputBlock from './ScrapInputBlock/ScrapInputBlock';
import { urlVerifier } from '../../../utils/string';
import PunchCardModal from '../../punch-card/components/PunchCardModal/PunchCardModal';
import Benefits from '../../home/components/HomeLanding/Benefits/Benefits';
import Loading from '../../../elements/Loading/Loading';
import CarouselProducts from '../../../elements/CarouselProducts/CarouselProducts';
import GenericStore from '../GenericStore/GenericStore';
import environment from '../../../environment';
import { Helmet } from 'react-helmet';

import style from './Store.scss';
import coverUSImg from '../../../assets/img/home_meta_US_cover.png';
import coverPEImg from '../../../assets/img/home_meta_PE_cover.png';
import coverCOImg from '../../../assets/img/home_meta_CO_cover.png';

import type { OnlineStore, WishList } from '../../../entities';
import type { Match } from '../../../router';
import type { LocaleState } from '../../locale/duck';
import type { LocalValuesState } from '../../local-values/duck';
import type { HTTPError } from '../../../error';


type Props = {
  +store?: OnlineStore;
  +isFetchingStore?: boolean;
  +fetchStore: (name: string) => void;
  +match: Match;
  +locale: LocaleState;
  +localValues: LocalValuesState;
  +chosenVariants?: { [key: string]: number };
  +goToRedirect: (route: string) => void;
  +resetState: () => void;
  +coupon?: string;
  +isClaimingPoints?: boolean;
  +claimPointsError?: HTTPError;
  +isFetchingWishList1: boolean;
  +isFetchingWishList2: boolean;
  +wishList1: WishList;
  +wishList2: WishList;
  +fetchWishList1Error: HTTPError;
  +fetchWishList2Error: HTTPError;
  +getUserWishList1: (userId: string, nameSlug: string) => void;
  +getUserWishList2: (userId: string, nameSlug: string) => void;
};

type State = {
  isBrowser: boolean;
  isOpenRewardModal: boolean;
};

const messages = defineMessages({
  US: {
    titleOne: {
      id: 'store.document.title.one',
    },
    titleTwo: {
      id: 'store.document.title.US.two',
    },
    descriptionOne: {
      id: 'store.document.description.one',
    },
    descriptionTwo: {
      id: 'store.document.description.US.two',
    },
  },
  PE: {
    titleOne: {
      id: 'store.document.title.one',
    },
    titleTwo: {
      id: 'store.document.title.PE.two',
    },
    descriptionOne: {
      id: 'store.document.description.one',
    },
    descriptionTwo: {
      id: 'store.document.description.PE.two',
    },
  },
  CO: {
    titleOne: {
      id: 'store.document.title.one',
    },
    titleTwo: {
      id: 'store.document.title.CO.two',
    },
    descriptionOne: {
      id: 'store.document.description.one',
    },
    descriptionTwo: {
      id: 'store.document.description.CO.two',
    },
  },
  default: {
    titleOne: {
      id: 'store.document.title.one',
    },
    titleTwo: {
      id: 'store.document.title.two',
    },
    descriptionOne: {
      id: 'store.document.description.one',
    },
    descriptionTwo: {
      id: 'store.document.description.two',
    },
  },
});

const countriesImages = {
  US: coverUSImg,
  PE: coverPEImg,
  CO: coverCOImg,
};

export default class Store extends Component<Props, State> {
  state = {
    isBrowser: false,
    isOpenRewardModal: false,
  };

  componentDidMount() {
    const { fetchStore, store, match, getUserWishList1, getUserWishList2, wishList1, wishList2 } = this.props;
    if (!store) {
      fetchStore(match.params.name || '');
    }
    if (!wishList1) {
      /* Note: test */
      // getUserWishList1('U09452357', 'especial-laptops');
      /* Note: production */
      getUserWishList1('U09452357', 'productos-populares-de-amazon');
    }

    if (!wishList2) {
      /* Note: test */
      // getUserWishList2('U09452357', 'especial-laptops');
      /* Note: production */
      getUserWishList2('U09452357', 'productos-destacados-de-amazon');
    }

    this.setState({
      isBrowser: true,
    });
  }

  componentWillUnmount() {
    const { resetState } = this.props;
    resetState();
  }

  handleOpenModal = () => this.setState({ isOpenRewardModal: true });

  handleCloseModal = () => this.setState({ isOpenRewardModal: false });

  render() {
    const {
      store, locale, goToRedirect, localValues, isClaimingPoints, claimPointsError, coupon,
      isFetchingStore, wishList1, wishList2, match, chosenVariants,
    } = this.props;
    const { isBrowser, isOpenRewardModal } = this.state;

    const descriptionOne = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).descriptionOne);
    const descriptionTwo = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).descriptionTwo);
    const description = `${descriptionOne} ${match.params.name} ${descriptionTwo}`;
    const titleOne = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).titleOne);
    const titleTwo = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).titleTwo);
    const title = `${titleOne} ${match.params.name} ${titleTwo}`
    const baseUrl = environment.baseUrl[locale.domainCountry || 'US'];
    const coverImg = countriesImages[locale.domainCountry || 'US'];
    return (
      <Fragment>
        <Helmet meta={[
          { name: 'description', content: description },
          { property: 'og:image', content: `${baseUrl}${coverImg}` },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { name: 'twitter:title', content: title },
          { name: 'twitter:twitter:image', content: `${baseUrl}${coverImg}` },
          { name: 'twitter:twitter:description', content: description },
        ]}>
          <title>{title}</title>
        </Helmet>
        <section className={style.storeWrap}>
          {isFetchingStore && <Loading type='sk-three-bounce'
                                       className={style.modalLoading}
                                       number={3}/>}
          {!!store && store.slug !== 'amazon' &&
          <GenericStore store={store}
                        match={match}
                        localValues={localValues}
                        locale={locale}
                        goToRedirect={goToRedirect}/>}
          {!!store && store.slug === 'amazon' &&
          <section>
            <ScrapInputBlock store={store}
                             locale={locale}
                             localValues={localValues}
                             handleClickButton={(input: string) => {
                               if (urlVerifier(input) || input.startsWith('http')) {
                                 goToRedirect(`/new-order/product?initialQuery=${input}`);
                               } else {
                                 goToRedirect(`/new-order/search?name=${input}`);
                               }
                             }}/>
            <section className={style.landing}>
              <section className={style.rowWrap}>
                <Benefits/>
              </section>
              {!!wishList1 && wishList1.items.length > 4 &&
              <section className={style.rowWrap}>
                <CarouselProducts title="store.products.amazon.title.one"
                                  url={`/wish-list/${wishList1.user.id}/${wishList1.slug}`}
                                  items={wishList1.items}/>
              </section>}
              {!!wishList2 && wishList2.items.length > 4 &&
              <section className={style.rowWrap}>
                <CarouselProducts title="store.products.amazon.title.two"
                                  url={`/wish-list/${wishList2.user.id}/${wishList2.slug}`}
                                  items={wishList2.items}/>
              </section>}
              {store && <section className={style.whyBuyFrom}>
                <FormattedMessage id="store.question.amazon">{
                  text => <h2>{text}</h2>
                }</FormattedMessage>
                <FormattedMessage id="store.answer.amazon">{
                  text => <p>{text}</p>
                }</FormattedMessage>
              </section>}
            </section>
          </section>}
          {/*<PunchCardModal isOpen={isOpenRewardModal}
                          isClaimingPoints={isClaimingPoints}
                          handleClose={this.handleCloseModal}
                          claimPointsError={claimPointsError}
                          coupon={coupon}/>*/}
        </section>
      </Fragment>);
  }
}

Store.propTypes = {
  store: PropTypes.object,
  isFetchingStore: PropTypes.bool,
  fetchStore: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  goToRedirect: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  coupon: PropTypes.string,
  isClaimingPoints: PropTypes.bool,
  claimPointsError: PropTypes.object,
  isFetchingWishList1: PropTypes.bool,
  isFetchingWishList2: PropTypes.bool,
  wishList1: PropTypes.object,
  wishList2: PropTypes.object,
  fetchWishList1Error: PropTypes.object,
  fetchWishList2Error: PropTypes.object,
  getUserWishList1: PropTypes.func.isRequired,
  getUserWishList2: PropTypes.func.isRequired,
};

Store.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/store/components/Store.jsx